import { Link } from 'react-router-dom'
import type { ConexaoDto, UsuarioSessao } from '@crm/shared'
import { useConexoes } from '../api/hooks-chat'
import { IconeWhatsApp } from './icones'
import { pode } from '../permissao'
import { formatarDataHora } from '../formato'

/** Número que não está recebendo mensagem agora. */
export function forasDoAr(conexoes: ConexaoDto[] | undefined): ConexaoDto[] {
  return (conexoes ?? []).filter((c) => c.status !== 'conectado')
}

/**
 * Os números fora do ar, escritos como a equipe os reconhece.
 *
 * O número sozinho não identifica: quem tem três linhas não sabe de cabeça
 * qual é a do comercial. O apelido cadastrado vem junto, entre parênteses.
 */
export function nomesDosForasDoAr(fora: ConexaoDto[]): string {
  const nomes = fora.map((c) => (c.nome ? `${c.numero} (${c.nome})` : c.numero))
  if (nomes.length <= 2) return nomes.join(' e ')
  return `${nomes.slice(0, 2).join(', ')} e mais ${nomes.length - 2}`
}

/**
 * Faixa vermelha no topo quando um número de WhatsApp cai.
 *
 * Sessão do WhatsApp cai sozinha: o celular desliga por dias, alguém remove o
 * aparelho conectado, e o número para de receber sem nada acontecer na tela.
 * Aconteceu aqui e ninguém percebeu por onze dias, porque o único sinal era
 * uma bolinha de 8px na coluna do bate-papo.
 *
 * Acima da barra de navegação, e não dentro de uma página: o aviso precisa
 * aparecer em qualquer tela, inclusive para quem passa o dia no Leads.
 * Vermelho porque cliente escrevendo sem ninguém receber é perda de venda em
 * curso, não um aviso que pode esperar.
 */
export function AvisoDeConexao({ usuario }: { usuario: UsuarioSessao }) {
  // Confere sozinho de minuto em minuto: a volta também precisa ser percebida
  // sem ninguém recarregar a página.
  const { data: conexoes } = useConexoes({ intervalo: 60_000 })
  const fora = forasDoAr(conexoes)

  if (fora.length === 0) return null

  // Reconectar é ler o QR de novo: é de quem pode mudar os números (ADR 0038).
  const podeReconectar = pode(usuario, 'configuracao.ver') && pode(usuario, 'configuracao.numeros')
  const primeiro = fora[0] as ConexaoDto

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-red-600 px-4 py-1.5 text-sm text-white">
      <IconeWhatsApp className="h-4 w-4 shrink-0" />
      {/*
        O número vem escrito no aviso, e não só a contagem: saber que "2
        números caíram" não diz se o que caiu é o do comercial ou o de teste.
      */}
      <span className="font-medium">
        {fora.length === 1 ? 'Fora do ar: ' : `${fora.length} números fora do ar: `}
        {nomesDosForasDoAr(fora)}
      </span>
      {/*
        `ultimaConexaoEm` é quando o número conectou pela última vez, e não
        quando caiu: dizer "sem receber desde" ali seria inventar uma hora que
        o sistema não sabe. O que ele sabe, e o que importa agora, é que
        mensagem nova não está entrando.
      */}
      <span className="text-white/80">
        As mensagens que chegarem agora não entram no CRM.
        {fora.length === 1 && primeiro.ultimaConexaoEm
          ? ` Última conexão em ${formatarDataHora(primeiro.ultimaConexaoEm)}.`
          : ''}
      </span>
      {podeReconectar ? (
        <Link
          to="/configuracao?aba=numeros"
          className="rounded-md bg-superficie px-2 py-0.5 text-xs font-semibold text-red-700 transition hover:bg-red-50"
        >
          Reconectar agora
        </Link>
      ) : null}
    </div>
  )
}
